import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, Compass, Rocket, Users2, Target, Lightbulb, TrendingUp, Globe, Heart, Award, CheckCircle, Sparkles, ArrowRight, Brain, Zap, Shield, Building, X } from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, Navigation, EffectFade } from 'swiper/modules';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import 'swiper/css/effect-fade';


const Vision = () => {
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [animatedStats, setAnimatedStats] = useState({});

  const heroSlides = [
    {
      title: "Powering Business with Reliable IT Infrastructure",
      subtitle: "Our vision is to be the most trusted IT infrastructure partner for enterprises across Asia",
      image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=2072",
      highlight: "Our Vision"
    },
    {
      title: "Built on Precision. Driven by Service.",
      subtitle: "Our mission: deliver enterprise-grade IT solutions that businesses depend on every day",
      image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?q=80&w=2070",
      highlight: "Our Mission"
    },
    {
      title: "Infrastructure That Never Lets You Down",
      subtitle: "Creating lasting impact through reliable networks, secure systems, and connected workplaces",
      image: "https://images.unsplash.com/photo-1573164713988-8665fc963095?q=80&w=2069",
      highlight: "Our Commitment"
    }
  ];

  const stats = [
    { icon: Target, value: 98, suffix: '%', label: 'Client Satisfaction', description: 'Commitment to excellence' },
    { icon: Globe, value: 6, suffix: '', label: 'Core Services', description: 'End-to-end IT portfolio' },
    { icon: Rocket, value: 500, suffix: '+', label: 'Projects Delivered', description: 'Successful engagements' },
    { icon: TrendingUp, value: 15, suffix: '+', label: 'Years Experience', description: 'Deep industry expertise' },
  ];

  const visionPoints = [
    {
      icon: Brain,
      title: 'Cutting-Edge Solutions',
      description: 'We stay ahead by adopting the latest technologies and best practices in networking, AV, and data center infrastructure.',
      color: 'from-blue-500 to-cyan-500',
      impact: 'Future-ready infrastructure'
    },
    {
      icon: Globe,
      title: 'Japan-Wide Reach',
      description: 'Expanding our on-site engineering coverage across Japan to serve enterprises wherever they operate.',
      color: 'from-green-500 to-emerald-500',
      impact: 'Nationwide service coverage'
    },
    {
      icon: Lightbulb,
      title: 'Innovation in Delivery',
      description: 'Continuously refining our delivery methodology to reduce project timelines and minimize disruption to your operations.',
      color: 'from-purple-500 to-pink-500',
      impact: 'Faster, smarter delivery'
    },
    {
      icon: Users2,
      title: 'Long-Term Partnerships',
      description: 'Building relationships that go beyond the project — with maintenance contracts, SLAs, and proactive support.',
      color: 'from-orange-500 to-red-500',
      impact: 'Ongoing client success'
    },
  ];

  const commitments = [
    {
      icon: Rocket,
      title: 'Delivery Excellence',
      description: 'We are committed to on-time, on-budget delivery with zero surprises. Every project follows a structured methodology with clear milestones, regular client updates, and full documentation at handover.',
      initiatives: ['Structured Project Plans', 'Milestone Reviews', 'As-Built Documentation'],
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: Shield,
      title: 'Security & Compliance',
      description: 'Every network and infrastructure solution we deploy adheres to industry security standards — from firewall hardening and VLAN segmentation to access controls and compliance documentation.',
      initiatives: ['Security Hardening', 'VLAN Segmentation', 'Compliance Reporting'],
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: Heart,
      title: 'Client-First Service',
      description: 'We treat every client as a long-term partner. Our team is responsive, communicative, and dedicated to understanding your unique environment and goals before any tool is touched.',
      initiatives: ['Dedicated Account Managers', 'Proactive Communication', '24/7 Support Options'],
      color: 'from-red-500 to-orange-500'
    },
    {
      icon: Shield,
      title: 'Ethical Practices',
      description: 'We maintain the highest standards of integrity, transparency, and fairness in all our dealings, ensuring trust and respect in every interaction.',
      initiatives: ['Transparent Pricing', 'Fair Negotiations', 'Privacy Protection'],
      color: 'from-green-500 to-emerald-500'
    },
    {
      icon: Zap,
      title: 'Speed & Efficiency',
      description: 'We respect your time by mobilizing quickly, executing efficiently, and delivering results without compromising quality or safety.',
      initiatives: ['Fast Mobilization', 'Quick Response', 'Efficient Processes'],
      color: 'from-yellow-500 to-orange-500'
    },
    {
      icon: Award,
      title: 'Quality Assurance',
      description: 'Every deployment undergoes rigorous testing and certification to ensure reliability, security, and long-term performance.',
      initiatives: ['Pre-Delivery Testing', 'Certification Reports', 'Post-Handover Review'],
      color: 'from-indigo-500 to-purple-500'
    },
  ];

  const futureGoals = [
    {
      year: '2025',
      title: 'Technology Certifications',
      description: 'Achieve additional vendor certifications across Cisco, Fortinet, Poly, and other leading enterprise technology brands.',
      icon: Brain,
      milestones: ['Cisco CCNP', 'Fortinet NSE', 'Poly Certified']
    },
    {
      year: '2026',
      title: 'Regional Office Expansion',
      description: 'Establish dedicated operations centers in Osaka and Nagoya to serve enterprise clients in western and central Japan.',
      icon: Globe,
      milestones: ['Site Selection', 'Team Hiring', 'Operations Launch']
    },
    {
      year: '2027',
      title: 'Managed Services',
      description: 'Launch a comprehensive managed IT services offering including monitoring, maintenance, and 24/7 helpdesk support.',
      icon: Lightbulb,
      milestones: ['Service Design', 'Platform Build', 'Client Rollout']
    },
    {
      year: '2028',
      title: 'APAC Expansion',
      description: 'Extend our enterprise IT services footprint to Singapore, South Korea, and Southeast Asia.',
      icon: Users2,
      milestones: ['Market Entry', 'Local Partnerships', 'Regional Teams']
    },
  ];

  const impactAreas = [
    { area: 'Projects Completed', impact: '500+', icon: Users2, growth: 'Since 2020' },
    { area: 'Enterprise Clients', impact: '150+', icon: Building, growth: 'Long-term partners' },
    { area: 'Regions Served', impact: '5+', icon: Globe, growth: 'Across Japan' },
    { area: 'Client Satisfaction', impact: '98%', icon: TrendingUp, growth: 'Avg. CSAT score' },
  ];

  // Animate stats on mount
  useEffect(() => {
    stats.forEach((stat, index) => {
      setTimeout(() => {
        let current = 0;
        const increment = stat.value / 50;
        const timer = setInterval(() => {
          current += increment;
          if (current >= stat.value) {
            current = stat.value;
            clearInterval(timer);
          }
          setAnimatedStats(prev => ({
            ...prev,
            [index]: current.toFixed(0)
          }));
        }, 30);
      }, index * 200);
    });
  }, []);

  const GoalModal = ({ goal, onClose }) => (
    <AnimatePresence>
      {goal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            className="bg-white rounded-2xl max-w-2xl w-full p-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button onClick={onClose} className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-full">
              <X className="h-6 w-6" />
            </button>
            
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl mb-6">
              <goal.icon className="h-8 w-8 text-white" />
            </div>
            
            <h2 className="text-3xl font-bold mb-2">{goal.year}</h2>
            <h3 className="text-2xl font-bold text-blue-600 mb-4">{goal.title}</h3>
            <p className="text-gray-600 mb-6 text-lg leading-relaxed">{goal.description}</p>
            
            <div className="mb-6">
              <h4 className="font-bold mb-3">Key Milestones</h4>
              <div className="space-y-2">
                {goal.milestones.map((milestone, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0" />
                    <span className="text-gray-700">{milestone}</span>
                  </div>
                ))}
              </div>
            </div>

            <Link to="/roadmap">
              <Button className="w-full bg-gradient-to-r from-blue-600 to-cyan-500">
                Learn More About Our Roadmap
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <>
      <Helmet>
        <title>Vision & Mission - Tasks IT Services | Our Commitment to Tech Excellence</title>
        <meta name="description" content="Discover Tasks IT Services' vision and mission: to be the most trusted IT infrastructure partner for enterprises in Japan, delivering reliable networks, data centers, and AV systems." />
      </Helmet>

      {/* Enhanced Hero Section with Carousel */}
      <section className="relative h-screen overflow-hidden">
        <Swiper
          modules={[Autoplay, Pagination, Navigation, EffectFade]}
          effect="fade"
          autoplay={{ delay: 5000, disableOnInteraction: false }}
          pagination={{ clickable: true }}
          navigation
          className="h-full"
        >
          {heroSlides.map((slide, index) => (
            <SwiperSlide key={index}>
              <div className="relative h-full">
                <div className="absolute inset-0">
                  <img 
                    src={slide.image} 
                    alt={slide.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-indigo-900/95 via-purple-900/90 to-blue-800/85"></div>
                  <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:32px_32px]"></div>
                </div>

                <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
                  <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="max-w-4xl text-white text-center mx-auto"
                  >
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.4 }}
                      className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6 border border-white/20"
                    >
                      <Sparkles className="h-4 w-4 text-yellow-300" />
                      <span className="text-sm font-medium">{slide.highlight}</span>
                    </motion.div>

                    <motion.h1
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 }}
                      className="text-6xl md:text-7xl font-bold mb-6 leading-tight"
                    >
                      {slide.title}
                    </motion.h1>

                    <motion.p
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.6 }}
                      className="text-2xl text-blue-100 mb-8 leading-relaxed"
                    >
                      {slide.subtitle}
                    </motion.p>

                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.7 }}
                      className="flex flex-wrap justify-center gap-4"
                    >
                      <Link to="/contact">
                        <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50 shadow-2xl text-lg px-8 py-6 group">
                          Join Our Journey
                          <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                        </Button>
                      </Link>
                      <Link to="/about">
                        <Button size="lg" className="bg-blue-500 text-white border-2 border-white hover:bg-blue-600 shadow-xl text-lg px-8 py-6">
                          Learn More
                        </Button>
                      </Link>
                    </motion.div>
                  </motion.div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </section>



      {/* Vision & Mission Cards */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-purple-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:32px_32px]"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.03 }}
              className="bg-white/10 backdrop-blur-sm p-10 rounded-3xl border-2 border-white/20 hover:bg-white/20 transition-all"
            >
              <motion.div
                whileHover={{ rotate: 360 }}
                transition={{ duration: 0.6 }}
                className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full mb-6 shadow-2xl"
              >
                <Eye className="h-10 w-10 text-white" />
              </motion.div>
              <h2 className="text-4xl font-bold mb-6">Our Vision</h2>
              <p className="text-blue-100 text-lg leading-relaxed mb-6">
                By 2030, our goal is to serve large enterprises with the same level of excellence demonstrated by Japan’s leading tech recruitment firms.
              </p>
              <p className="text-blue-200 leading-relaxed">
                
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.03 }}
              className="bg-white/10 backdrop-blur-sm p-10 rounded-3xl border-2 border-white/20 hover:bg-white/20 transition-all"
            >
              <motion.div
                whileHover={{ rotate: 360 }}
                transition={{ duration: 0.6 }}
                className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-green-400 to-cyan-500 rounded-full mb-6 shadow-2xl"
              >
                <Compass className="h-10 w-10 text-white" />
              </motion.div>
              <h2 className="text-4xl font-bold mb-6">Our Mission</h2>
              <p className="text-blue-100 text-lg leading-relaxed mb-6">
                We aim to seamlessly connect talented professionals with businesses by leveraging our agility, technical insight, and people-focused approach — ensuring organizations receive the precise expertise they need to meet their IT objectives.
              </p>
              <p className="text-blue-200 leading-relaxed">
                
              </p>
            </motion.div>
          </div>
        </div>
      </section>

     

     
     
      <GoalModal goal={selectedGoal} onClose={() => setSelectedGoal(null)} />

      

      {/* Looking Ahead Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-600 px-4 py-2 rounded-full mb-6">
                <Sparkles className="h-4 w-4" />
                <span className="text-sm font-semibold">The Future</span>
              </div>
              <h2 className="text-4xl font-bold mb-6">Looking Ahead</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                As technology continues to reshape our world, we are striving to grow into a leader in recruitment innovation. Our vision goes beyond matching people to roles — we aim to create a future where every tech professional finds meaningful work that challenges and inspires them.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Together with our community of candidates and clients, we are not simply filling positions — we are contributing to the future of work in the tech industry, one exceptional placement at a time.
              </p>
              <Link to="/contact">
                <Button size="lg" className="bg-gradient-to-r from-blue-600 to-purple-600 text-white group">
                  Be Part of Our Journey
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-purple-400 rounded-3xl blur-3xl opacity-20"></div>
              <img
                src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?q=80&w=2072"
                alt="Future technology"
                className="relative rounded-2xl shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:32px_32px]"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            >
              <Target className="h-20 w-20 mx-auto mb-6 text-blue-200" />
            </motion.div>
            <h2 className="text-5xl md:text-6xl font-bold mb-6">
              Ready to Build Better Infrastructure?
            </h2>
            <p className="text-2xl text-blue-100 mb-10 max-w-2xl mx-auto leading-relaxed">
              Whether you’re scaling your operations or looking for skilled professionals, we can shape the future together through our exceptional recruitment approach.
            </p>
            <div className="flex flex-wrap justify-center gap-6">
              <Link to="/contact">
                <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50 shadow-2xl text-xl px-10 py-7 group">
                  Get Started Today
                  <ArrowRight className="ml-2 h-6 w-6 group-hover:translate-x-2 transition-transform" />
                </Button>
              </Link>
              <Link to="/services">
                <Button size="lg" className="bg-blue-500 text-white border-2 border-white hover:bg-blue-600 shadow-2xl text-xl px-10 py-7">
                  Explore Services
                </Button>
              </Link>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="mt-12 flex flex-wrap justify-center gap-8 text-blue-100"
            >
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5" />
                <span>Free consultation</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5" />
                <span>Expert guidance</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5" />
                <span>Proven results</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Vision;